import React, { useContext, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavigationUtils } from 'lib/navigationUtilities';
import { AuthContext } from 'components/user/AuthContext';
import { MessageLoading, MessageError, MessageEmpty, MessageToast } from 'components/element/MessageTypes';
import { Operations } from './lib/Operations';
import UserPageHeader from 'components/user/PageHeader';
import TabNavigation from 'components/element/TabNavigation';
import Twut from './Twut';
import CreateTwut from './CreateTwut';
import api from 'lib/api';

const TwutsList = () => {
  const { isLoggedIn } = useContext(AuthContext);
  const { urlUsername, locationPathName, navigateToThread } = NavigationUtils();

  const isRepliesView = locationPathName.includes('/replies');
  const userProfileActiveTab = isRepliesView ? 'replies' : 'twuts';

  const isFollowingView = locationPathName.includes('/twuts/following');
  const homeFeedActiveTab = isFollowingView ? 'following' : 'for-you';

  const userProfileTabs = [
    { id: 'twuts', label: 'Twuts', path: '' },
    { id: 'replies', label: 'Replies', path: '/replies' }
  ];

  const homeFeedTabs = [
    { id: 'for-you', label: 'For You', path: '/' },
    { id: 'following', label: 'Following', path: '/twuts/following' }
  ];

  const queryKey = urlUsername
    ? ['twutsList', urlUsername, userProfileActiveTab]
    : ['twutsList', homeFeedActiveTab];

  const {
    toast, clearToast, isProcessing,
    createTwut, updateTwut, deleteTwut, replyTwut
  } = Operations(queryKey, {
    onReplySuccess: navigateToThread
  });

  const {
    data,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey,
    queryFn: () => {
      if (urlUsername) {
        return isRepliesView ? api.user.getRepliesWithParents(urlUsername) : api.user.profileWithTwuts(urlUsername);
      }

      return isFollowingView ? api.twut.listFollowing() : api.twut.list();
    },
    staleTime: 1000 * 60,
  });

  useEffect(() => {
    refetch();
  }, [isLoggedIn, userProfileActiveTab, homeFeedActiveTab, refetch]);

  const handleReply = async (parentId, data) => {
    await replyTwut(parentId, data);
    navigateToThread(parentId);
  };

  if (isLoading) return <MessageLoading message="Loading Twuts..." />;
  if (isError) return <MessageError message="Error fetching Twuts" error={error} />;

  let twuts, userProfile;

  if (urlUsername) {
    if (isRepliesView) {
      twuts = data?.replies || [];
      userProfile = data?.user;
    } else {
      twuts = data?.twuts || [];
      userProfile = data?.user;
    }
  } else {
    twuts = data || [];
    userProfile = null;
  }

  let emptyMessage;
  if (isRepliesView) {
    emptyMessage = `${urlUsername} hasn't replied to any twuts yet`;
  } else if (urlUsername) {
    emptyMessage = `${urlUsername} hasn't posted anything yet`;
  } else if (isFollowingView) {
    emptyMessage = "You're not following anyone yet";
  } else {
    emptyMessage = 'Nothing to see here...';
  }

  const renderTwutItem = (twut) => {
    return (
      <Twut
        key={twut.id}
        twut={twut}
        onUpdate={updateTwut}
        onDelete={deleteTwut}
        onReply={handleReply}
        isUpdating={isProcessing}
      />
    );
  };

  const renderReplyItem = (reply) => {
    return (
      <div key={reply.id} className="mb-6 pb-3 border-b-2 border-gray-less">
        {reply.parent_twut && (
          <Twut twut={reply.parent_twut} isReadOnly={true} />
        )}

        <div className="pl-4 mt-2">
          {renderTwutItem(reply)}
        </div>
      </div>
    );
  };

  return (
    <div>
      <MessageToast toast={toast} onClose={clearToast} />

      {userProfile && (
        <UserPageHeader user={userProfile} />
      )}

      {urlUsername ? (
        <TabNavigation tabs={userProfileTabs} activeTab={userProfileActiveTab} baseUrl={`/${urlUsername}`} />
      ) : (
        <TabNavigation tabs={isLoggedIn ? homeFeedTabs : []} activeTab={homeFeedActiveTab} baseUrl="" />
      )}

      {isLoggedIn && !urlUsername && (
        <CreateTwut onSubmit={createTwut} isPosting={isProcessing} />
      )}

      {twuts && twuts.length > 0 ? (
        <div>
          {isRepliesView ? twuts.map(renderReplyItem) : twuts.map(renderTwutItem)}
        </div>
      ) : (
        <MessageEmpty message={emptyMessage} />
      )}
    </div>
  );
};

export default TwutsList;
